import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import type { TextInput } from "react-native";
import { useControlledField } from "../../utils/useControlledField";
import { useMultiValueField } from "../../utils/useMultiValueField";
import { useUsesSheet } from "../Dropdown/useInSheet";
import { type FieldValidation, MultiFieldFooter } from "../FieldShell";
import { Input } from "../Input";
import { Listbox, type ListboxOption, type ListboxOptionProps } from "../Listbox";
import { YStack } from "../Stacks";
import { type ComboboxFilterFn, defaultComboboxFilter, useComboboxFilter } from "./useComboboxFilter";

// One option shape, owned by Listbox — aliased so Combobox can't drift from it.
export type ComboboxOption = ListboxOption;
type ComboboxBaseProps = {
  /** Escape hatch for custom rows; Listbox still owns selection, keyboard, and
   *  ids. Spread `optionProps` onto a `ListboxOption` (use its
   *  `leading`/`trailing` slots) or your own element. */
  renderOption?: (option: ComboboxOption, optionProps: ListboxOptionProps) => ReactNode;
  /** Controlled filter text. Omit for the uncontrolled (internal-state) path. */
  inputValue?: string;
  onInputChange?: (text: string) => void;
  /** Empty-state copy when nothing matches. @default "No results" */
  emptyText?: string;
  /** Error-state copy when an async load fails. @default "Couldn't load options" */
  errorText?: string;
  /** External in-flight flag, OR-ed with the internal async pending state. */
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  /** Inline validation state forwarded to the trigger field. @default "none" */
  validation?: FieldValidation;
  /** Helper / error copy rendered under the trigger field. */
  helperText?: string;
  /** Fires once per engagement when the field is left: input blur (web) or sheet
   *  close (touch — dismiss or pick; the display trigger can't blur). Lets a form
   *  adapter mark the field touched so a blur-gated required message can reveal. */
  onBlur?: () => void;
};

// Single vs multi is a discriminated union. Multi is always chips-below (the
// search field owns the trigger, so the count can't live there); picking toggles
// and keeps the overlay open.
type ComboboxSingleProps = {
  multiple?: false;
  value?: string;
  onValueChange: (value: string) => void;
  values?: never;
  onValuesChange?: never;
};
type ComboboxMultiProps = {
  multiple: true;
  values?: string[];
  onValuesChange?: (values: string[]) => void;
  value?: never;
  onValueChange?: never;
};
export type ComboboxProps = ComboboxBaseProps & (ComboboxSingleProps | ComboboxMultiProps) & ({
  options: ComboboxOption[];
  /** Predicate run per option against the query. @default case-insensitive substring on `label` */
  filter?: ComboboxFilterFn;
  loadOptions?: never;
} | {
  /** Options are fetched (debounced, stale-cancelled) instead of filtered locally. */
  loadOptions: (query: string) => Promise<ComboboxOption[]>;
  options?: never;
  filter?: never;
}) &
// Require at least one accessible name — a visible `label`, an `aria-label`, or
// both — so the combobox is never nameless to screen readers.
({
  label: string;
  "aria-label"?: string;
} | {
  label?: string;
  "aria-label": string;
});
const DEFAULT_EMPTY_TEXT = "No results";
const DEFAULT_ERROR_TEXT = "Couldn't load options";
// Wait for the sheet's open animation before focusing its search input, so the
// keyboard rises with the surface already settled.
const SHEET_SEARCH_FOCUS_DELAY_MS = 350;
export const Combobox = (props: ComboboxProps) => {
  const {
    options = [],
    inputValue,
    onInputChange,
    filter = defaultComboboxFilter,
    emptyText = DEFAULT_EMPTY_TEXT,
    errorText = DEFAULT_ERROR_TEXT,
    loadOptions,
    loading,
    disabled = false,
    required = false,
    placeholder,
    label,
    "aria-label": ariaLabel,
    validation = "none",
    helperText,
    onBlur,
    renderOption
  } = props;
  const multiple = props.multiple === true;
  const singleValue = props.multiple === true ? undefined : props.value;
  const onValueChange = props.multiple === true ? undefined : props.onValueChange;
  // Controlled/uncontrolled set, shared with Select — picks render even when the
  // parent omits `values` (the half-controlled trap this fixes).
  const multi = useMultiValueField({
    values: props.multiple === true ? props.values : undefined,
    onValuesChange: props.multiple === true ? props.onValuesChange : undefined,
    // Local options only — async-loaded tags degrade to the raw id (out of scope
    // for this stack), so the chip labels don't chase `visibleOptions` per keystroke.
    options
  });
  const {
    values: selectedValues,
    setValues,
    removeValue,
    selectedItems
  } = multi;
  const listboxId = useId();
  const generatedHelperId = useId();
  // Multi renders the helper below the chips (outside the field), so the
  // trigger↔helper link is wired by id here; single lets the Input own it.
  const helperTextId = multiple && helperText ? generatedHelperId : undefined;
  const inputField = useControlledField<string>({
    value: inputValue,
    defaultValue: "",
    onChange: onInputChange
  });
  const setInputValue = inputField.setValue;
  const query = inputField.value ?? "";
  const [focused, setFocused] = useState(false);
  // Tracks an explicit dismissal (select / Escape) so focus + a non-empty option
  // set don't immediately re-open the overlay. Mirrors Tooltip's `dismissed` flag.
  const [dismissed, setDismissed] = useState(false);
  // Touch: tap opens the sheet (header = search); the display-only trigger shows
  // `committedLabel`, kept apart from `query` so a dismissed search can't strand it.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [committedLabel, setCommittedLabel] = useState("");
  const searchRef = useRef<TextInput>(null);
  // Web trigger input — the focus target when removing the last chip (multi),
  // so a keyboard user lands back on the field instead of the document body.
  const webTriggerRef = useRef<TextInput>(null);
  const focusWebTrigger = useCallback(() => {
    webTriggerRef.current?.focus();
  }, []);
  // The last `value` reflected into the text — lets the reflect effect tell an
  // external value change apart from our own commit (kept in sync on select).
  const reflectedValue = useRef<string | undefined>(undefined);
  // Native sheet vs web popover — drives the trigger mode, open source, and
  // search slot. Web (including iOS Safari) stays on the popover so a Combobox
  // inside a Dialog remains tappable; see `useUsesSheet`.
  const usesSheet = useUsesSheet();
  const {
    visibleOptions,
    loading: isLoading,
    error: isError
  } = useComboboxFilter({
    options,
    query,
    filter,
    loadOptions,
    loading,
    // Gate async fetches on engagement, so mounting a form full of async pickers
    // doesn't fire an empty-query load for each before the user opens any.
    enabled: !disabled && (focused || sheetOpen)
  });
  const hasContent = visibleOptions.length > 0 || isLoading || isError || query.length > 0;
  const open = usesSheet ? sheetOpen && !disabled : focused && !dismissed && !disabled && hasContent;

  // Exactly-once guard for `onBlur` (same contract as Select): web reports from
  // input blur; the sheet reports from close, where a pick and the surface's own
  // close request can both land. Re-arms on the next engagement.
  const blurReported = useRef(false);
  const reportBlur = useCallback(() => {
    if (!blurReported.current) {
      blurReported.current = true;
      onBlur?.();
    }
  }, [onBlur]);

  // Single: reflect the picked label into the field and dismiss. Multi: the
  // Listbox hands back the toggled set — just forward it and keep the overlay open.
  const handleSelect = useCallback((selectedValue: string) => {
    const option = visibleOptions.find(candidate => candidate.value === selectedValue);
    if (!option) {
      return;
    }
    // Popover: the trigger is the search field, so reflect the pick into it.
    // Sheet: the trigger shows `committedLabel`; `query` stays the search text.
    if (!usesSheet) {
      setInputValue(option.label);
    }
    setCommittedLabel(option.label);
    // Keep the reflect ref in lockstep with the pick so a controlled parent that
    // rejects or transforms the value re-reflects instead of stranding the label.
    reflectedValue.current = option.value;
    onValueChange?.(option.value);
    setDismissed(true);
    setSheetOpen(false);
    // A pick ends the sheet engagement (the display trigger can't blur). Web
    // keeps focus in the search input, so its blur reports later as usual.
    if (usesSheet) {
      reportBlur();
    }
  }, [usesSheet, visibleOptions, setInputValue, onValueChange, reportBlur]);
  const handleValuesChange = useCallback((next: string[]) => {
    if (disabled) {
      return;
    }
    setValues(next);
    // Multi keeps the overlay open after a pick; reset the filter so the next
    // search starts fresh instead of staying narrowed to the just-toggled match.
    setInputValue("");
  }, [disabled, setValues, setInputValue]);

  // Reflect the controlled `value` into the input text — single only; multi has
  // no single committed label (the picks live in the chips below). On mount and
  // whenever it changes while the user isn't typing (unfocused). Async option
  // sets may match later; the value stays un-reflected until a match exists.
  const matchableOptions = loadOptions === undefined ? options : visibleOptions;
  useEffect(() => {
    if (multiple || focused || singleValue === undefined) {
      return;
    }
    if (singleValue === reflectedValue.current) {
      return;
    }
    if (singleValue === "") {
      reflectedValue.current = singleValue;
      setCommittedLabel("");
      if (!usesSheet && query !== "") {
        setInputValue("");
      }
      return;
    }
    const match = matchableOptions.find(option => option.value === singleValue);
    if (match === undefined) {
      return;
    }
    reflectedValue.current = singleValue;
    setCommittedLabel(match.label);
    if (!usesSheet && match.label !== query) {
      setInputValue(match.label);
    }
  }, [multiple, usesSheet, focused, singleValue, query, matchableOptions, setInputValue]);

  // Sheet: focus the in-sheet search once the open animation settles.
  useEffect(() => {
    if (!usesSheet || !open) {
      return;
    }
    const id = setTimeout(() => searchRef.current?.focus(), SHEET_SEARCH_FOCUS_DELAY_MS);
    return () => clearTimeout(id);
  }, [usesSheet, open]);
  const handleChangeText = useCallback((text: string) => {
    setInputValue(text);
    setDismissed(false);
  }, [setInputValue]);
  const handleFocus = useCallback(() => {
    setFocused(true);
    blurReported.current = false;
  }, []);

  // Discard an uncommitted edit on blur: snap back to the selection (or empty).
  // Multi keeps `committedLabel` empty, so blur clears the search query.
  const handleBlur = useCallback(() => {
    setFocused(false);
    setInputValue(committedLabel);
    reportBlur();
  }, [committedLabel, setInputValue, reportBlur]);

  // Popover open is derived from focus/dismissed, so ignore the surface's close
  // requests: the trigger re-fires one on the click that focuses (and opens) the
  // field, which would dismiss it instantly. Real closes come from blur (outside
  // click), Escape (handled on the input), and selection. Keyboard ArrowDown when
  // closed still opens via `next === true`. Sheet drives `sheetOpen` directly.
  const handleOpenChange = useCallback((next: boolean) => {
    if (usesSheet) {
      setSheetOpen(next);
      if (next) {
        // Open the sheet on a fresh, unfiltered search.
        setInputValue("");
        setDismissed(false);
        blurReported.current = false;
      } else {
        // Sheet dismiss (swipe-down / outside tap) is the field's blur on
        // touch — the display trigger has no DOM blur to lean on.
        reportBlur();
      }
      return;
    }
    if (next) {
      setDismissed(false);
    }
  }, [usesSheet, setInputValue, reportBlur]);

  // Web: the search field is itself the trigger (focus-driven popover). Listbox
  // stamps the combobox role + aria-expanded/controls/activedescendant + keyboard.
  const webTrigger = <Input ref={webTriggerRef} label={label} required={required} aria-label={ariaLabel} placeholder={placeholder} value={query} onChangeText={handleChangeText} onFocus={handleFocus} onBlur={handleBlur}
  // Only when multi owns an external helper id. Single lets Input's own
  // useTextFieldShell set aria-describedby; passing undefined here would land
  // after Input's {...controlProps} spread and wipe that a11y link.
  {...helperTextId ? {
    "aria-describedby": helperTextId
  } : {}}
  // Escape closes the open list (the surface's own close is ignored above);
  // composes with the Listbox keyboard stamped onto this same trigger.
  onKeyDown={event => {
    if (event.key === "Escape") {
      event.preventDefault();
      setDismissed(true);
    }
  }} disabled={disabled} validation={validation}
  // Multi renders the helper below the chips (via FieldHelperRow) so it
  // doesn't sit between the field and its value chips; single keeps it here.
  helperText={multiple ? undefined : helperText} aria-autocomplete="list" autoComplete="off" />;

  // Touch: display-only trigger. Single shows the committed label; multi shows a
  // count (the picks render as chips below). Open via an explicit frame `onPress`;
  // the inner control stays `pointerEvents="none"` so the press lands on the frame,
  // not the input. (Don't rely on the tap falling through to Popover.Trigger —
  // synthetic touch can't actuate it.)
  const touchTrigger = <Input pointerEvents="none" onPress={() => handleOpenChange(true)} label={label} required={required} aria-label={ariaLabel} {...helperTextId ? {
    "aria-describedby": helperTextId
  } : {}} placeholder={placeholder} value={multiple ? selectedValues.length > 0 ? `${selectedValues.length} selected` : "" : committedLabel} disabled={disabled} validation={validation} helperText={multiple ? undefined : helperText} />;

  // Touch: the live search input, pinned in the sheet header above the results.
  const searchHeader = <Input ref={searchRef} placeholder={placeholder} value={query} onChangeText={handleChangeText} disabled={disabled} role="combobox" aria-expanded={open} aria-controls={listboxId} aria-autocomplete="list" autoComplete="off" />;

  // The prop union guarantees one of label/aria-label; rebuild it for Listbox.
  const nameProps: {
    label: string;
  } | {
    "aria-label": string;
  } = label !== undefined ? {
    label
  } : {
    "aria-label": ariaLabel ?? ""
  };
  const listboxOptions = <Listbox.Options renderOption={renderOption} loading={isLoading} error={isError} emptyText={emptyText} errorText={errorText} />;

  // Single vs multi differ by 3 selection props; spread them into one Listbox.
  const selectionProps = multiple ? {
    multiple: true as const,
    values: selectedValues,
    onValuesChange: handleValuesChange
  } : {
    value: singleValue ?? null,
    onValueChange: handleSelect
  };
  return <YStack data-sentry-element="YStack" data-sentry-component="Combobox" data-sentry-source-file="Combobox.tsx">
      <Listbox {...selectionProps} options={visibleOptions} open={open} onOpenChange={handleOpenChange} trigger={usesSheet ? touchTrigger : webTrigger} typeToFilter sheetHeader={usesSheet && open ? searchHeader : undefined} sheetFullHeight id={listboxId} {...nameProps} data-sentry-element="Listbox" data-sentry-source-file="Combobox.tsx">
        {listboxOptions}
      </Listbox>
      {multiple ? <MultiFieldFooter items={selectedItems} onRemove={removeValue} groupLabel={label ?? ariaLabel ?? ""} disabled={disabled} validation={validation} helperText={helperText} helperTextId={helperTextId} focusAfterLastRemove={focusWebTrigger} /> : null}
    </YStack>;
};